import { useApolloClient } from '@apollo/client';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useUserDelete } from './useUserDelete';

type UserDeleteModalProps = {
  shouldRedirect: boolean;
};

export const useUserDeleteModal = ({ shouldRedirect }: UserDeleteModalProps) => {
  const { t } = useTranslation();
  const { deleteUser } = useUserDelete();
  const { open, setLoading, close } = useConfirmationDialog();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();

  return useCallback(
    ({ id, name }) => {
      open({
        confirmationButtonColor: 'error',
        confirmationLabel: t('Delete'),
        content: (
          <DialogContent dividers>
            <Typography>
              {t('You’re about to delete the following user from')} <strong>OpenX Select</strong>:
            </Typography>
            <Typography variant="body1Bold" sx={{ mb: 3 }}>
              {name}
            </Typography>
            <Typography>{t('Do you want to proceed?')}</Typography>
          </DialogContent>
        ),
        customContent: true,
        onConfirmed: async () => {
          try {
            setLoading(true);
            await deleteUser(id);

            apolloClient.cache.evict({ fieldName: 'user', id: 'ROOT_QUERY' });
            if (shouldRedirect) navigate('/users/');
            enqueueSnackbar('User deleted successfully', { variant: 'success' });
          } catch (error) {
            const defaultErrorMessage = t('Failed to delete User');
            const message = error[0]?.message;

            enqueueSnackbar(message ?? defaultErrorMessage, { variant: 'error' });
          } finally {
            setLoading(false);
            close();
          }
        },
        title: t('Delete User'),
      });
    },
    [close, open, setLoading, t, deleteUser, enqueueSnackbar, navigate, shouldRedirect, apolloClient]
  );
};
